'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePWADetection } from '@/utils/hooks/usePWADetection';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { X, Search, Star, MoreVertical, Trash2, StarIcon } from 'lucide-react';
import { historyService, type HistoryEntry, type HistorySettings } from '@/lib/history-service';
import { getLanguages } from '@/app/[locale]/actions/languages';
import { getLanguageVarieties, getLanguageScripts } from '@/app/[locale]/actions/language-varieties';
import { cn } from '@/lib/utils';

interface TranslationHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEntry: (entry: HistoryEntry) => void;
}

interface DeleteButtonProps {
  onDelete: () => void;
  disabled?: boolean;
}

function DeleteButton({ onDelete, disabled }: DeleteButtonProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={disabled}
      onClick={onDelete}
      className="h-8 w-8 p-0"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );
}

export function TranslationHistory({ isOpen, onClose, onSelectEntry }: TranslationHistoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const t = useTranslations('history');
  const locale = useLocale();
  const queryClient = useQueryClient();
  const { isMobile } = usePWADetection();

  // Fetch languages for name mapping
  const { data: languages = [] } = useQuery({
    queryKey: ['languages', locale],
    queryFn: () => getLanguages(locale),
    staleTime: Infinity
  });

  // Fetch history - only when dialog is open
  const { data: history = [] } = useQuery({
    queryKey: ['translationHistory'],
    queryFn: () => historyService.getHistory(),
    enabled: isOpen, // Only fetch when dialog is open
    staleTime: 0, // Always refetch when dialog opens to get latest data
    gcTime: 0, // Don't cache when dialog is closed
    refetchOnWindowFocus: false // Don't refetch on window focus
  });

  // Fetch history settings - only when dialog is open
  const { data: historySettings } = useQuery({
    queryKey: ['historySettings'],
    queryFn: () => historyService.getHistorySettings(),
    enabled: isOpen, // Only fetch when dialog is open
    staleTime: 30000 // 30 seconds
  });

  // Create language name mapping
  const languageMap = useMemo(() => {
    return new Map(languages.map(lang => [lang.id, lang.name]));
  }, [languages]);

  // Fetch varieties and scripts for all target languages in history
  const uniqueTargetLanguages = useMemo(() => {
    const langSet = new Set<string>();
    history.forEach(entry => {
      if (entry.targetVarietyId || entry.targetScriptId) {
        langSet.add(entry.targetLanguageId);
      }
    });
    return Array.from(langSet);
  }, [history]);

  // Fetch varieties and scripts for languages that need them
  const varietyQueries = useQuery({
    queryKey: ['varietiesForHistory', uniqueTargetLanguages, locale],
    queryFn: async () => {
      const varietyMap = new Map<string, string>();
      const scriptMap = new Map<string, string>();

      for (const langId of uniqueTargetLanguages) {
        try {
          const [varieties, scripts] = await Promise.all([
            getLanguageVarieties(langId, locale),
            getLanguageScripts(langId, locale)
          ]);

          varieties.forEach(variety => varietyMap.set(variety.id, variety.name));
          scripts.forEach(script => scriptMap.set(script.id, script.name));
        } catch (error) {
          console.error(`Failed to fetch varieties/scripts for language ${langId}:`, error);
        }
      }

      return { varieties: varietyMap, scripts: scriptMap };
    },
    enabled: isOpen && uniqueTargetLanguages.length > 0,
    staleTime: 600000 // 10 minutes
  });

  // Enhanced history with language names
  const enhancedHistory = useMemo(() => {
    const varietyMap = varietyQueries.data?.varieties || new Map();
    const scriptMap = varietyQueries.data?.scripts || new Map();

    return history.map(entry => ({
      ...entry,
      sourceLangName: languageMap.get(entry.sourceLanguageId) || entry.sourceLanguageId,
      targetLangName: languageMap.get(entry.targetLanguageId) || entry.targetLanguageId,
      targetVarietyName: entry.targetVarietyId ? varietyMap.get(entry.targetVarietyId) : undefined,
      targetScriptName: entry.targetScriptId ? scriptMap.get(entry.targetScriptId) : undefined
    }));
  }, [history, languageMap, varietyQueries.data]);

  // Filter history based on search and favorites
  const filteredHistory = useMemo(() => {
    let filtered = enhancedHistory;

    if (showFavoritesOnly) {
      filtered = filtered.filter(entry => entry.isFavorite);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.sourceText.toLowerCase().includes(query) ||
        entry.targetText.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [enhancedHistory, searchQuery, showFavoritesOnly]);

  const hasFavorites = enhancedHistory.some(entry => entry.isFavorite);

  // Reset search when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setShowFavoritesOnly(false);
    }
  }, [isOpen]);

  const handleToggleFavorite = async (entryId: string, currentFavorite: boolean) => {
    await historyService.updateHistoryEntry(entryId, { isFavorite: !currentFavorite });
    // Invalidate and refetch the history
    queryClient.invalidateQueries({ queryKey: ['translationHistory'] });
  };

  const handleDeleteEntry = async (entryId: string) => {
    setIsProcessing(true);
    await historyService.deleteHistoryEntry(entryId);
    // Invalidate and refetch the history
    queryClient.invalidateQueries({ queryKey: ['translationHistory'] });
    setIsProcessing(false);
  };

  const handleClearAllHistory = async () => {
    setIsProcessing(true);
    await historyService.clearAllHistory();
    // Invalidate and refetch the history
    queryClient.invalidateQueries({ queryKey: ['translationHistory'] });
    setIsProcessing(false);
  };

  const handleToggleHistoryEnabled = async () => {
    const newSettings: Partial<HistorySettings> = {
      isEnabled: !historySettings?.isEnabled
    };
    await historyService.updateHistorySettings(newSettings);
    queryClient.invalidateQueries({ queryKey: ['historySettings'] });
  };

  const handleSelectEntry = (entry: HistoryEntry) => {
    onSelectEntry(entry);
    onClose();
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  // Truncate text to specified number of lines
  const truncateText = (text: string, maxLines: number = 3) => {
    // Replace line breaks with spaces and limit to ~150 chars per 3 lines
    const cleanText = text.replace(/\s+/g, ' ').trim();
    const maxLength = maxLines * 80; // Rough estimate
    return cleanText.length > maxLength
      ? cleanText.substring(0, maxLength) + '...'
      : cleanText;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="h-[100dvh] lg:h-[85vh] max-h-[100dvh] lg:max-h-[90vh] w-[100dvw] lg:w-[85vw] max-w-[100dvw] lg:max-w-5xl flex flex-col p-0 lg:p-3 m-0 lg:m-auto"
        onOpenAutoFocus={isMobile ? (e) => e.preventDefault() : undefined}
      >
        <div className="p-4 pb-0">
          <DialogHeader className="flex-shrink-0">
            <div className="flex flex-col md:flex-row md:items-end gap-2">
              <DialogTitle className='text-start'>{t('title')}</DialogTitle>
              <div className="text-xs text-start text-muted-foreground">
                {t('localStorageNote')}
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="flex-shrink-0 space-y-4 px-4">
          {/* Search and controls */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 focus-pocus"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSearch}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>

            {hasFavorites && (
              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                size="icon"
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={cn(showFavoritesOnly && "text-yellow-500")}
              >
                <Star className={cn("h-4 w-4", showFavoritesOnly && "fill-yellow-500")} />
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Button
                    variant="ghost"
                    className="w-full justify-start h-auto px-2 py-1.5 font-normal"
                    onClick={() => setShowClearConfirm(true)}
                  >
                    {t('clearAllHistory')}
                  </Button>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Button
                    variant="ghost"
                    className="w-full justify-start h-auto px-2 py-1.5 font-normal"
                    onClick={handleToggleHistoryEnabled}
                  >
                    {historySettings?.isEnabled ? t('turnOffHistory') : t('turnOnHistory')}
                  </Button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* History list */}
        <ScrollArea className="flex-1 mt-4 px-4">
          {filteredHistory.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-center">
              <div className="text-muted-foreground">
                {searchQuery ? t('noMatches') : t('noHistoryYet')}
              </div>
            </div>
          ) : (
            <div className="space-y-2 pb-4 lg:pb-6">
              {filteredHistory.map((entry, index) => (
                <div
                  key={entry.id}
                  className="p-2 lg:p-4 rounded-lg lg:border cursor-pointer transition-colors hover:bg-muted/50"
                  onClick={() => handleSelectEntry(entry)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">
                        {entry.sourceLangName} → {entry.targetLangName}
                      </div>
                      {(entry.targetVarietyName || entry.targetScriptName) && (
                        <div className="flex gap-1">
                          {entry.targetVarietyName && (
                            <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                              {entry.targetVarietyName}
                            </Badge>
                          )}
                          {/* {entry.targetScriptName && (
                            <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                              {entry.targetScriptName}
                            </Badge>
                          )} */}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleFavorite(entry.id, entry.isFavorite)}
                        className="h-8 w-8 p-0"
                      >
                        <StarIcon
                          className={cn(
                            "h-4 w-4",
                            entry.isFavorite ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground"
                          )}
                        />
                      </Button>
                      <DeleteButton
                        onDelete={() => handleDeleteEntry(entry.id)}
                        disabled={isProcessing}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm line-clamp-3">
                      {truncateText(entry.sourceText)}
                    </div>
                    <div className="text-sm text-muted-foreground line-clamp-3">
                      {truncateText(entry.targetText)}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground mt-2">
                    {new Date(entry.createdAt).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>

      {/* Confirmation dialog */}
      <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('clearAllHistoryConfirmTitle')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t('clearAllHistoryConfirmDescription')}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowClearConfirm(false)}
              disabled={isProcessing}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                handleClearAllHistory();
                setShowClearConfirm(false);
              }}
              disabled={isProcessing}
            >
              {t('clearAll')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
